import { Container, Singleton } from 'typescript-ioc/es5'
import { AppService } from './app'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiDeleteResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { DomainSecret } from '@pushly/models/lib/structs/domain-secrets/domain-secret'
import { CreateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/create-domain-secret-request.dto'
import { UpdateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/update-domain-secret-request.dto'
import { DomainSecretType } from '@pushly/models/lib/enums/domain-secret-type'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { ApiVersion } from '../enums/api-version.enum'

@Singleton
export class DomainSecretsService extends AbstractRequestService {
    public appService: AppService

    public constructor() {
        super()
        this.appService = Container.get(AppService)
    }

    public async fetchAll(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<DomainSecret[]>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.request(HttpMethod.GET, this.getEndpoint(domainId, options.version), {
                ...options,
                config: { skipCaseConversion: true },
                responseType: [DomainSecret],
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async create(
        domainId: number,
        secret: CreateDomainSecretRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<DomainSecret>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const payload: Record<string, string | undefined> = {
                alias: secret.alias,
                type: secret.type as any,
            } as any

            if (secret.type === DomainSecretType.USERPASS) {
                payload.username = secret.username
                payload.password = secret.password
            } else {
                payload.value = secret.value
            }

            return this.request(HttpMethod.POST, this.getEndpoint(domainId, options.version), {
                ...options,
                data: payload,
                responseType: DomainSecret,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async updateSecret(
        domainId: number,
        secretId: number,
        secret: UpdateDomainSecretRequestDto,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<DomainSecret>> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            const payload: Record<string, string | undefined> = {}

            if (secret.username !== undefined || secret.password !== undefined) {
                payload.username = secret.username
                payload.password = secret.password
            } else {
                payload.value = secret.value
            }

            return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId, options.version)}/${secretId}`, {
                ...options,
                data: payload,
                responseType: DomainSecret,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    public async deleteSecret(
        domainId: number,
        secretId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiDeleteResponse> {
        if (options.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        try {
            return this.requestDelete(`${this.getEndpoint(domainId, options.version)}/${secretId}`, {
                ...options,
            })
        } finally {
            if (options.showLoadingScreen) {
                this.appService.unsetModuleLoading()
            }
        }
    }

    private getEndpoint = (id: number, version: ApiVersion | undefined) => `${aqe.defaults.publicApiDomain}/${version}domains/${id}/secrets`
}
